import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Minus, Send, Loader2, RotateCcw, GraduationCap, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { _t } from '@/i18n/t';
import { getAiLimitMessage, notifyAiLimit } from '@/shared/utils/apiError';
import { callUIAPI, callUIAPIStream } from '../../utils/apiUtils';
import * as apiConstants from '../../utils/apiUtilsConstants';
import { useAssistantConfig } from './hooks/useAssistantConfig';
import TrainingFeedback from './TrainingFeedback';
import AssistantMessageContent from './AssistantMessageContent';
import AssistantWelcome from './AssistantWelcome';
import MarcoAvatar from './MarcoAvatar';
import { buildGreeting } from './greeting';

export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Set on the LAST chunk of a multi-chunk response so training feedback
   *  attaches to the complete reply. */
  triggerQuestion?: string;
  /** Set on the last chunk. Includes `fullReply` in streaming mode so trainers
   *  see/edit the full assembled response. */
  metadata?: {
    sources?: string[];
    fullReply?: string;
    intent?: string;
    confidence_score?: number | null;
    confidence_level?: string | null;
    suggest_handoff?: boolean;
    gradingId?: string | null;
    [key: string]: unknown;
  };
}

interface NdjsonEvent {
  type?: string;
  content?: string;
  message?: string;
  errorCode?: string;
  metadata?: AssistantMessage['metadata'];
}

interface SupportAssistantWindowProps {
  onClose: () => void;
  firstName?: string | null;
  messages: AssistantMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AssistantMessage[]>>;
}

const SupportAssistantWindow = ({ onClose, firstName, messages, setMessages }: SupportAssistantWindowProps) => {
  const navigate = useNavigate();

  const greeting = useMemo(() => buildGreeting(firstName), [firstName]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [trainingActive, setTrainingActive] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // ── Handoff state — per-conversation, resets on handleReset ──────────
  const [handoffInitiated, setHandoffInitiated] = useState(false);
  const [showHandoffOffer, setShowHandoffOffer] = useState(false);
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const [handoffConfirmation, setHandoffConfirmation] = useState<string | null>(null);
  const handoffTriggerRef = useRef<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Tracks an in-flight streaming AbortController so we can cancel on reset/unmount.
  const inFlightAbortRef = useRef<AbortController | null>(null);

  const { config } = useAssistantConfig();
  const trainingEnabled = config?.trainingEnabled === true;
  const chatCopyEnabled = config?.chatCopyEnabled === true;
  const supportRequestEnabled = config?.supportRequestEnabled === true;

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showHandoffOffer, handoffConfirmation]);

  useEffect(() => {
    if (!isLoading) inputRef.current?.focus();
  }, [isLoading]);

  useEffect(() => {
    return () => {
      inFlightAbortRef.current?.abort();
    };
  }, []);

  /* ── Ctrl+Shift+T to toggle training mode ────────────────────────── */
  useEffect(() => {
    if (!trainingEnabled) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        setTrainingActive((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [trainingEnabled]);

  const copyConversation = useCallback(async () => {
    const lines: string[] = [];
    const divider = '─'.repeat(48);

    lines.push(_t('assistant.transcript.heading'));
    lines.push(_t('assistant.transcript.exported', { timestamp: new Date().toLocaleString() }));
    lines.push(divider);
    lines.push('');

    for (const msg of messages) {
      const label =
        msg.role === 'user'
          ? `👤 ${_t('assistant.transcript.user')}`
          : `🤖 ${_t('assistant.transcript.assistant')}`;
      lines.push(label);
      // For assistant messages with fullReply metadata, prefer the complete
      // assembled response over the last chunk content.
      const content =
        msg.role === 'assistant' && msg.metadata?.fullReply
          ? msg.metadata.fullReply
          : msg.content;
      lines.push(content);

      if (msg.metadata) {
        const meta = msg.metadata;
        const parts: string[] = [];
        if (meta.intent) parts.push(`intent: ${meta.intent}`);
        if (meta.confidence_score != null) parts.push(`confidence: ${meta.confidence_score}`);
        if (meta.confidence_level) parts.push(`level: ${meta.confidence_level}`);
        if (meta.suggest_handoff) parts.push('suggest_handoff: true');
        if (meta.sources?.length) parts.push(`sources: [${meta.sources.join(', ')}]`);
        if (parts.length) {
          lines.push(`  [metadata] ${parts.join(' | ')}`);
        }
      }

      lines.push('');
    }

    lines.push(divider);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback for older browsers / insecure contexts
      const textarea = document.createElement('textarea');
      textarea.value = lines.join('\n');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [messages]);

  /**
   * Read an NDJSON stream from a fetch Response. Calls onEvent for each
   * parsed JSON object on its own line. Returns when the stream closes.
   */
  const readNdjsonStream = async (
    response: Response,
    onEvent: (event: NdjsonEvent) => void,
    signal: AbortSignal
  ) => {
    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        if (signal.aborted) {
          try { await reader.cancel(); } catch { }
          break;
        }
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines; keep any partial trailing line in the buffer.
        let newlineIdx;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          if (!line) continue;
          try {
            onEvent(JSON.parse(line) as NdjsonEvent);
          } catch {
            // Malformed line — skip. Should not happen with a healthy server.
          }
        }
      }
      // Flush any trailing partial line (server should always end with \n,
      // but defensive).
      const trailing = buffer.trim();
      if (trailing) {
        try { onEvent(JSON.parse(trailing) as NdjsonEvent); } catch { }
      }
    } finally {
      try { reader.releaseLock(); } catch { }
    }
  };

  const submitSupportRequest = useCallback(
    async (triggerType: 'confidence_based' | 'user_requested') => {
      if (handoffInitiated || handoffSubmitting) return;

      setHandoffSubmitting(true);
      setShowHandoffOffer(false);

      try {
        const lastMetadataMsg = [...messages].reverse().find((m) => m.metadata);
        const meta = lastMetadataMsg?.metadata;

        const response = await callUIAPI(
          'assistant/support-request',
          {
            conversationHistory: messages
              .filter((m) => m.content !== greeting)
              .map(({ role, content }) => ({ role, content })),
            triggerQuestion: handoffTriggerRef.current || '',
            triggerType,
            confidenceAssessment: meta
              ? {
                  confidence_score: meta.confidence_score,
                  confidence_level: meta.confidence_level,
                }
              : {},
            retrievedChunkIds: meta?.sources || [],
          },
          apiConstants.METHOD_POST,
          true
        );

        if (response.data?.success) {
          setHandoffInitiated(true);
          setHandoffConfirmation(
            "We've notified our support team — they'll follow up with you by email shortly."
          );
        } else {
          console.error('Support request returned unexpected response:', response.data);
        }
      } catch (err) {
        console.error('Support request submission failed:', err);
      } finally {
        setHandoffSubmitting(false);
      }
    },
    [handoffInitiated, handoffSubmitting, messages, greeting]
  );

  const handleHandoffSuggestion = useCallback(
    (suggestHandoff: boolean | undefined, userMessage: string) => {
      if (suggestHandoff && supportRequestEnabled && !handoffInitiated) {
        handoffTriggerRef.current = userMessage;
        setShowHandoffOffer(true);
      }
    },
    [supportRequestEnabled, handoffInitiated]
  );

  const handleResponseMetadata = useCallback(
    (metadata: AssistantMessage['metadata'], userMessage: string) => {
      if (!metadata) return;

      if (metadata.intent === 'user-requested-contact' && supportRequestEnabled) {
        if (handoffInitiated) {
          setHandoffConfirmation(
            "We've already notified our support team about this conversation — they'll be in touch soon."
          );
        } else {
          handoffTriggerRef.current = userMessage;
          submitSupportRequest('user_requested');
        }
        return true;
      }

      handleHandoffSuggestion(metadata.suggest_handoff, userMessage);
      return false;
    },
    [supportRequestEnabled, handoffInitiated, submitSupportRequest, handleHandoffSuggestion]
  );

  const applyDeferredGrade = useCallback(
    async (gradingId: string, userMessage: string) => {
      try {
        const response = await callUIAPI(`assistant/grade/${gradingId}`, undefined, apiConstants.METHOD_GET, true);
        const grade = response.data;
        if (!grade) return;

        setMessages((prev) =>
          prev.map((m) => (m.metadata?.gradingId === gradingId ? { ...m, metadata: { ...m.metadata, ...grade } } : m))
        );
        handleHandoffSuggestion(grade.suggest_handoff, userMessage);
      } catch {
        return;
      }
    },
    [setMessages, handleHandoffSuggestion]
  );

  const resetTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const sendMessage = useCallback(async (presetMessage?: string) => {
    const text = (presetMessage ?? input).trim();
    if (!text || isLoading) return;

    setShowHandoffOffer(false);
    setHandoffConfirmation(null);

    const userMsg: AssistantMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    if (presetMessage === undefined) {
      setInput('');
      resetTextareaHeight();
    }
    setIsLoading(true);

    const historyForAPI = updatedMessages
      .filter((m) => m.content !== greeting)
      .map(({ role, content }) => ({ role, content }));

    const abortController = new AbortController();
    inFlightAbortRef.current = abortController;

    try {
      const response = await callUIAPIStream(
        'assistant/chat',
        { message: text, history: historyForAPI, handoffInitiated },
        apiConstants.METHOD_POST
      );

      if (!response.ok) {
        const status = response.status;
        let errorText: string;
        if (status === 402) {
          let errorCode: string | undefined;
          try {
            errorCode = (await response.json())?.errorCode;
          } catch { }
          notifyAiLimit({ errorCode });
          errorText = getAiLimitMessage({ errorCode }) ?? _t('assistant.error.generic');
        } else if (status === 503) {
          errorText = _t('assistant.error.unavailable');
        } else if (status === 400) {
          let serverMsg: string | undefined;
          try {
            const errBody = await response.json();
            serverMsg = errBody?.message;
          } catch { }
          errorText = serverMsg || _t('assistant.error.invalidRequest');
        } else {
          errorText = _t('assistant.error.generic');
        }
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: errorText, triggerQuestion: text },
        ]);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      const isStreaming = contentType.includes('application/x-ndjson');

      if (!isStreaming) {
        // ── Non-streaming path: server returned a single JSON object ──
        const data = await response.json();
        const { reply, metadata } = data;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: reply, triggerQuestion: text, metadata },
        ]);

        const contactRequested = handleResponseMetadata(metadata, text);
        if (!contactRequested && metadata?.gradingId) {
          void applyDeferredGrade(metadata.gradingId, text);
        }
        return;
      }

      // ── Streaming path: NDJSON events arrive as the LLM produces them ──
      let receivedChunkCount = 0;
      let errored = false;

      await readNdjsonStream(
        response,
        (event) => {
          if (event.type === 'chunk' && typeof event.content === 'string') {
            const chunkContent = event.content;
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: chunkContent },
            ]);
            receivedChunkCount++;
          } else if (event.type === 'done') {
            setMessages((prev) => {
              // Fallback: no chunks arrived but we have a fullReply in
              // metadata — surface it.
              if (receivedChunkCount === 0) {
                const fallback = event.metadata?.fullReply || _t('assistant.error.emptyResponse');
                return [
                  ...prev,
                  {
                    role: 'assistant',
                    content: fallback,
                    triggerQuestion: text,
                    metadata: event.metadata,
                  },
                ];
              }
              // Normal case: attach triggerQuestion + metadata to the last
              // assistant message that we already pushed.
              const next = prev.slice();
              const lastIdx = next.length - 1;
              if (next[lastIdx]?.role === 'assistant') {
                next[lastIdx] = {
                  ...next[lastIdx],
                  triggerQuestion: text,
                  metadata: event.metadata,
                };
              }
              return next;
            });

            handleResponseMetadata(event.metadata, text);
          } else if (event.type === 'error') {
            errored = true;
            notifyAiLimit(event);
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: getAiLimitMessage(event) || event.message || 'Unable to answer at the moment. Please try again later.',
                triggerQuestion: text,
              },
            ]);
          }
        },
        abortController.signal
      );

      // Stream ended without a done/error event AND no chunks — surface an error.
      if (receivedChunkCount === 0 && !errored && !abortController.signal.aborted) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: _t('assistant.error.generic'),
            triggerQuestion: text,
          },
        ]);
      }
    } catch {
      // Network-level failure (fetch threw). Silent on user-initiated abort.
      if (!abortController.signal.aborted) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: _t('assistant.error.generic'),
            triggerQuestion: text,
          },
        ]);
      }
    } finally {
      inFlightAbortRef.current = null;
      setIsLoading(false);
    }
  }, [input, isLoading, messages, greeting, setMessages, handoffInitiated, handleResponseMetadata, applyDeferredGrade]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const handleReset = () => {
    // Abort any in-flight stream so its chunks don't land in the new conversation.
    inFlightAbortRef.current?.abort();
    inFlightAbortRef.current = null;

    setMessages([{ role: 'assistant', content: greeting }]);
    setInput('');
    resetTextareaHeight();
    setIsLoading(false);

    setHandoffInitiated(false);
    setShowHandoffOffer(false);
    setHandoffSubmitting(false);
    setHandoffConfirmation(null);
    handoffTriggerRef.current = '';
  };

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const getHistoryUpTo = (index: number): Array<{ role: string; content: string }> => {
    return messages.slice(0, index).map(({ role, content }) => ({ role, content }));
  };

  const hasUserMessage = messages.some((msg) => msg.role === 'user');
  const popularRoutes = config?.popularRoutes ?? [];

  const endsAssistantRun = (index: number) => {
    if (messages[index]?.role !== 'assistant') return false;
    if (index === messages.length - 1) return !isLoading;
    return messages[index + 1].role !== 'assistant';
  };

  return (
    <div
      className={`fixed bottom-6 right-6 z-40 w-[380px] max-h-[min(640px,calc(100vh-3rem))] flex flex-col rounded-2xl shadow-2xl border border-border overflow-hidden transition-all duration-300 bg-card ${
        mounted
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-6 scale-95'
      }`}
    >
      {}
      <div
        className="flex items-center gap-2.5 px-3.5 py-3 shrink-0 border-b-2 border-primary-foreground/25 bg-primary"
      >
        <MarcoAvatar className="h-9 w-9 ring-2 ring-primary-foreground/60" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-primary-foreground leading-tight">
              {_t('assistant.name')}
            </p>
            {trainingActive && (
              <span
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-primary-foreground/15 text-primary-foreground/90"
                title={_t('assistant.training.active')}
              >
                <GraduationCap className="w-2.5 h-2.5" />
                {_t('assistant.training.badge')}
              </span>
            )}
          </div>
          <p className="text-[11px] text-primary-foreground/70 leading-tight mt-0.5">
            {_t('assistant.tagline')}
          </p>
        </div>
        {trainingEnabled && (
          <button
            type="button"
            onClick={() => setTrainingActive((prev) => !prev)}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              trainingActive
                ? 'text-primary-foreground bg-primary-foreground/20'
                : 'text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/15'
            }`}
            title={trainingActive ? _t('assistant.training.disable') : _t('assistant.training.enable')}
          >
            <GraduationCap className="w-3.5 h-3.5" />
          </button>
        )}
        {chatCopyEnabled && (
          <button
            type="button"
            onClick={copyConversation}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              copySuccess
                ? 'text-emerald-300 bg-primary-foreground/20'
                : 'text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/15'
            }`}
            title={copySuccess ? _t('assistant.action.copied') : _t('assistant.action.copy')}
          >
            {copySuccess ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={handleReset}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/15 transition-colors"
          title={_t('assistant.action.newConversation')}
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/15 transition-colors"
          title={_t('assistant.action.minimize')}
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {}
      <div
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0 scrollbar-subtle"
        aria-live="polite"
        data-testid="assistant-messages"
      >
        {!hasUserMessage && (
          <AssistantWelcome
            firstName={firstName}
            popularRoutes={popularRoutes}
            onRouteSelect={(message) => void sendMessage(message)}
          />
        )}

        {hasUserMessage && messages.map((msg, idx) => (
          <div key={idx}>
            <div
              className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' &&
                (endsAssistantRun(idx) ? (
                  <MarcoAvatar className="h-[26px] w-[26px]" />
                ) : (
                  <span className="w-[26px] shrink-0" aria-hidden="true" />
                ))}
              <div
                className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed rounded-2xl whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-secondary text-foreground rounded-bl-md'
                }`}
                data-testid="support-assistant-window-message"
              >
                <AssistantMessageContent
                  content={msg.content}
                  isAssistant={msg.role === 'assistant'}
                  onNavigate={handleNavigate}
                />
              </div>
            </div>

            {/* Training feedback controls — only on chunks that carry
                triggerQuestion (i.e. the final chunk of a response). In
                streaming mode, prefer fullReply from metadata so the
                trainer corrects the entire response, not the last chunk. */}
            {trainingActive &&
              msg.role === 'assistant' &&
              msg.triggerQuestion && (
                <TrainingFeedback
                  question={msg.triggerQuestion}
                  assistantResponse={msg.metadata?.fullReply || msg.content}
                  conversationHistory={getHistoryUpTo(idx)}
                  metadata={msg.metadata}
                  onFeedbackSubmitted={() => {}}
                />
              )}
          </div>
        ))}

        {}
        {isLoading && (
          <div className="flex items-end gap-2 justify-start">
            <MarcoAvatar className="h-[26px] w-[26px]" />
            <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-secondary">
              <div className="flex items-center gap-1">
                <span className="block w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
                <span className="block w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
                <span className="block w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        {}
        {showHandoffOffer && !handoffInitiated && (
          <div className="mx-1 p-3.5 rounded-xl border border-amber-200 bg-amber-50/60 animate-in fade-in slide-in-from-bottom-1 duration-300">
            <p className="text-[12.5px] leading-relaxed text-amber-900 mb-3">
              {_t('assistant.handoff.offer')}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => submitSupportRequest('confidence_based')}
                disabled={handoffSubmitting}
                data-th-button="true"
                className={`flex-1 px-3 py-1.5 rounded-lg border-none text-[12px] font-semibold text-primary-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  handoffSubmitting ? 'bg-primary/60' : 'bg-primary'
                }`}
              >
                {handoffSubmitting ? _t('assistant.handoff.sending') : _t('assistant.handoff.confirm')}
              </button>
              <button
                type="button"
                onClick={() => setShowHandoffOffer(false)}
                disabled={handoffSubmitting}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-muted-foreground border border-border hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {_t('assistant.handoff.decline')}
              </button>
            </div>
          </div>
        )}

        {}
        {handoffConfirmation && (
          <div className="mx-1 p-3 rounded-xl border border-emerald-200 bg-emerald-50/60 animate-in fade-in slide-in-from-bottom-1 duration-300">
            <p className="text-[12.5px] leading-relaxed text-emerald-800">
              {handoffConfirmation}
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {}
      <div className="shrink-0 border-t border-border px-3 py-2.5 bg-muted/40">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={_t('assistant.input.placeholder')}
            disabled={isLoading}
            rows={1}
            className="flex-1 resize-none bg-card border border-border rounded-xl px-3 py-2 text-[13px] text-foreground placeholder:text-placeholder-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring/50 disabled:opacity-50 transition-colors font-[inherit]"
            style={{ maxHeight: '80px', minHeight: '36px' }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 80) + 'px';
            }}
          />
          <button
            type="button"
            onClick={() => void sendMessage()}
            disabled={!input.trim() || isLoading}
            data-th-button="true"
            className={`w-9 h-9 rounded-xl border-none flex items-center justify-center shrink-0 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed ${
              input.trim() && !isLoading ? 'bg-primary' : 'bg-border'
            }`}
            title={_t('assistant.action.send')}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-primary-foreground animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-primary-foreground" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportAssistantWindow;